import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';
import * as yup from 'yup';
import {
    Doc31CouponItem,
    Doc31CouponItemInput,
    Doc31CouponPromotion,
    Doc31CouponPromotionCreate,
    Doc31CouponPromotionPatch,
    Doc31CouponSelectionStatus,
    Doc31PacingPeriod,
} from '../../../types/doc31';

export const COUPON_LANGUAGES = ['en', 'fr'] as const;
export type CouponLanguage = typeof COUPON_LANGUAGES[number];

export const DEFAULT_ALERT_PERCENT = 95;
export const LOCAL_DATE = 'YYYY-MM-DD';
const WALL_CLOCK = 'YYYY-MM-DDTHH:mm:ss';

export interface CouponFormItem {
    title: string;
    subTitle: string;
    description: string;
    thumbnail: string | null;
    background: string | null;
}

export interface CouponFormPeriod {
    periodStart: string;
    periodEnd: string;
    targetAwards: number | null;
}

export interface CouponForm {
    items: Record<CouponLanguage, CouponFormItem>;
    quantity: number | null;
    /** ISO instants. */
    startDate: string | null;
    endDate: string | null;
    isActive: boolean;
    provinceIds: number[];
    categoryId: number | null;
    activationCode: string;
    discountValue: number | null;
    rebateAmount: number | null;
    originalPrice: number | null;
    status: Doc31CouponSelectionStatus;
    weight: number | null;
    pacingTargets: CouponFormPeriod[];
    maxWinsPerParticipation: number | null;
    minimumAge: number | null;
    alertUtilizationPercent: number | null;
    servingUtilizationPercent: number | null;
}

function emptyItem(): CouponFormItem {
    return {
        title: '',
        subTitle: '',
        description: '',
        thumbnail: null,
        background: null,
    };
}

export function createCouponForm(window: {
    startDate: string | null;
    endDate: string | null;
}): CouponForm {
    return {
        items: { en: emptyItem(), fr: emptyItem() },
        quantity: null,
        startDate: window.startDate,
        endDate: window.endDate,
        isActive: true,
        provinceIds: [],
        categoryId: null,
        activationCode: '',
        discountValue: null,
        rebateAmount: 0,
        originalPrice: 0,
        status: 'draft',
        weight: 1,
        pacingTargets: [],
        maxWinsPerParticipation: null,
        minimumAge: null,
        alertUtilizationPercent: DEFAULT_ALERT_PERCENT,
        servingUtilizationPercent: null,
    };
}

function itemToForm(item: Doc31CouponItem | undefined): CouponFormItem {
    if (!item) {
        return emptyItem();
    }
    return {
        title: item.title,
        subTitle: item.subTitle ?? '',
        description: item.description ?? '',
        thumbnail: item.thumbnail,
        background: item.background,
    };
}

export function couponItem(
    coupon: Doc31CouponPromotion,
    language: CouponLanguage
): Doc31CouponItem | undefined {
    return coupon.items.find((item) => item.language === language);
}

export function couponToForm(coupon: Doc31CouponPromotion): CouponForm {
    const { selection } = coupon;
    return {
        items: {
            en: itemToForm(couponItem(coupon, 'en')),
            fr: itemToForm(couponItem(coupon, 'fr')),
        },
        quantity: coupon.quantity,
        startDate: coupon.startDate,
        endDate: coupon.endDate,
        isActive: coupon.isActive,
        provinceIds: [...coupon.provinceIds],
        categoryId: coupon.categoryId,
        activationCode: coupon.activationCode ?? '',
        discountValue: coupon.discountValue,
        rebateAmount: coupon.rebateAmount,
        originalPrice: coupon.originalPrice,
        status: selection.status,
        weight: selection.weight,
        pacingTargets: selection.pacingTargets.map((period) => ({
            ...period,
        })),
        maxWinsPerParticipation: selection.maxWinsPerParticipation,
        minimumAge: selection.minimumAge,
        alertUtilizationPercent: selection.alertUtilizationPercent,
        servingUtilizationPercent: selection.servingUtilizationPercent,
    };
}

function textOrNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

/**
 * The create body the form describes. Required numbers are typed as present:
 * the schema refuses a submit without them, and a comparison against a
 * coupon goes through this same function on both sides.
 */
export function formToBody(values: CouponForm): Doc31CouponPromotionCreate {
    return {
        startDate: values.startDate
            ? moment.utc(values.startDate).toISOString()
            : '',
        endDate: values.endDate ? moment.utc(values.endDate).toISOString() : '',
        isActive: values.isActive,
        activationCode: values.activationCode,
        quantity: values.quantity as number,
        categoryId: values.categoryId as number,
        provinceIds: [...values.provinceIds].sort((a, b) => a - b),
        items: COUPON_LANGUAGES.map(
            (language): Doc31CouponItemInput => {
                const item = values.items[language];
                return {
                    language,
                    title: item.title.trim(),
                    subTitle: textOrNull(item.subTitle),
                    description: textOrNull(item.description),
                    thumbnail: item.thumbnail,
                    background: item.background,
                };
            }
        ),
        discountValue: values.discountValue,
        rebateAmount: values.rebateAmount ?? 0,
        originalPrice: values.originalPrice ?? 0,
        selection: {
            status: values.status === 'withdrawn' ? undefined : values.status,
            weight: values.weight as number,
            pacingTargets: values.pacingTargets.map(
                (period): Doc31PacingPeriod => ({
                    periodStart: period.periodStart,
                    periodEnd: period.periodEnd,
                    targetAwards: period.targetAwards ?? 0,
                })
            ),
            maxWinsPerParticipation: values.maxWinsPerParticipation,
            minimumAge: values.minimumAge,
            alertUtilizationPercent:
                values.alertUtilizationPercent ?? DEFAULT_ALERT_PERCENT,
            servingUtilizationPercent: values.servingUtilizationPercent,
        },
    };
}

/** Only what the form changed against the saved coupon; `{}` for nothing. */
export function buildCouponPatch(
    coupon: Doc31CouponPromotion,
    values: CouponForm
): Doc31CouponPromotionPatch {
    const before = formToBody(couponToForm(coupon));
    const after = formToBody(values);
    const patch: Doc31CouponPromotionPatch = {};
    const target = patch as Record<string, unknown>;
    for (const key of Object.keys(after) as (keyof typeof after)[]) {
        if (key !== 'selection' && !isEqual(before[key], after[key])) {
            target[key] = after[key];
        }
    }
    const selection: Record<string, unknown> = {};
    for (const key of Object.keys(
        after.selection
    ) as (keyof typeof after.selection)[]) {
        if (!isEqual(before.selection[key], after.selection[key])) {
            selection[key] = after.selection[key];
        }
    }
    if (Object.keys(selection).length > 0) {
        patch.selection = selection;
    }
    return patch;
}

/** A picked wall-clock time read in the edition timezone, as an instant. */
export function wallClockToInstant(
    picked: moment.Moment,
    timezone: string
): string {
    return moment
        .tz(picked.format(WALL_CLOCK), WALL_CLOCK, timezone)
        .toISOString();
}

/** An instant as a picker value showing the edition's wall clock. */
export function instantToPicker(
    value: string | null,
    timezone: string
): moment.Moment | null {
    if (!value) {
        return null;
    }
    return moment(moment.tz(value, timezone).format(WALL_CLOCK), WALL_CLOCK);
}

/**
 * The week after the last period, or the first week of play when there is
 * none. Periods are inclusive: a week runs from its start to six days later.
 */
export function nextPacingWeek(
    periods: CouponFormPeriod[],
    firstDate: string
): CouponFormPeriod {
    const last = periods[periods.length - 1];
    const start = last?.periodEnd
        ? moment(last.periodEnd, LOCAL_DATE).add(1, 'day')
        : moment(firstDate, LOCAL_DATE);
    return {
        periodStart: start.format(LOCAL_DATE),
        periodEnd: start.clone().add(6, 'days').format(LOCAL_DATE),
        targetAwards: null,
    };
}

/** `Dec 1–7`, or `Nov 28 – Dec 4` across months. */
export function formatPeriod(start: string, end: string): string {
    const from = moment(start, LOCAL_DATE);
    const to = moment(end, LOCAL_DATE);
    if (from.isSame(to, 'month')) {
        return from.isSame(to, 'day')
            ? from.format('MMM D')
            : `${from.format('MMM D')}–${to.format('D')}`;
    }
    return `${from.format('MMM D')} – ${to.format('MMM D')}`;
}

/** Coupons in the draw and the weight they share. */
export function drawWeights(
    coupons: Doc31CouponPromotion[]
): {
    count: number;
    totalWeight: number;
} {
    const inDraw = coupons.filter(
        (coupon) => coupon.selection.status === 'active'
    );
    return {
        count: inDraw.length,
        totalWeight: inDraw.reduce(
            (sum, coupon) => sum + coupon.selection.weight,
            0
        ),
    };
}

/** A weight's share of the draw: `25%`, `33.3%`. */
export function drawShare(weight: number, totalWeight: number): string {
    const share = totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
    return `${share.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}

const itemSchema = yup.object({
    title: yup.string().trim().required('Add a title.'),
});

/** The API refuses anything below 0.01. */
export const PERCENT_MIN = 0.01;

const optionalPercent = yup
    .number()
    .nullable()
    .min(PERCENT_MIN, 'Use 0.01 to 100.')
    .max(100, 'Use 0.01 to 100.');

export const CouponFormSchema = yup.object({
    items: yup.object({ en: itemSchema, fr: itemSchema }),
    quantity: yup
        .number()
        .nullable()
        .required('Set how many coupons there are.')
        .integer('Use a whole number.')
        .min(1, 'Set at least 1.'),
    startDate: yup.string().nullable().required('Pick a start.'),
    endDate: yup
        .string()
        .nullable()
        .required('Pick an end.')
        .test('after-start', 'End after the start.', function (value) {
            const { startDate } = this.parent as CouponForm;
            return !value || !startDate || moment(value).isAfter(startDate);
        }),
    provinceIds: yup.array().min(1, 'Pick at least one province.'),
    categoryId: yup.number().nullable().required('Pick a category.'),
    activationCode: yup
        .string()
        .required('Enter the 12-digit activation code.')
        .matches(/^\d{12}$/, 'Enter the 12-digit activation code.'),
    discountValue: yup.number().nullable().min(0, 'Use 0 or more.'),
    rebateAmount: yup.number().nullable().min(0, 'Use 0 or more.'),
    originalPrice: yup.number().nullable().min(0, 'Use 0 or more.'),
    weight: yup
        .number()
        .nullable()
        .required('Set the odds weight.')
        .min(0, 'Use 0 or more.'),
    pacingTargets: yup.array(
        yup.object({
            periodStart: yup.string().required('Pick a start date.'),
            periodEnd: yup
                .string()
                .required('Pick an end date.')
                .test(
                    'not-before-start',
                    'End on or after the start.',
                    function (value) {
                        const { periodStart } = this.parent as CouponFormPeriod;
                        return !value || !periodStart || value >= periodStart;
                    }
                ),
            targetAwards: yup
                .number()
                .nullable()
                .required('Set a target.')
                .integer('Use a whole number.')
                .min(0, 'Use 0 or more.'),
        })
    ),
    maxWinsPerParticipation: yup
        .number()
        .nullable()
        .integer('Use a whole number.')
        .min(1, 'Set at least 1, or leave empty.'),
    minimumAge: yup
        .number()
        .nullable()
        .integer('Use a whole number.')
        .min(0, 'Use 0 or more.'),
    alertUtilizationPercent: optionalPercent,
    servingUtilizationPercent: optionalPercent,
});
